import { useEffect, useState, useCallback } from "react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { Container } from "@/components/site/Container";
import { ButtonLink } from "@/components/site/Button";
import { canonical } from "@/lib/seo";
import { listGalleryAssets, type GalleryAsset } from "@/lib/gallery.functions";
import { X, ChevronLeft, ChevronRight, Phone } from "lucide-react";

const TITLE = "Roofing Photo Gallery | Foam, Tile & Repair Jobs | Durafoam Roofing Phoenix";
const DESCRIPTION =
  "Browse real photos from Durafoam Roofing jobs across Phoenix — foam roof installation, recoating, tile R&R, leak repair, and skylight work. Family-owned since 1989.";

const CATEGORIES: Array<{ key: string; label: string }> = [
  { key: "all", label: "All" },
  { key: "foam-roofing", label: "Foam Roofing" },
  { key: "foam-recoating", label: "Foam Recoating" },
  { key: "tile-roofing", label: "Tile Roofing" },
  { key: "roof-repair", label: "Roof Repair" },
  { key: "roof-leaks", label: "Roof Leaks" },
  { key: "skylights", label: "Skylights" },
  { key: "commercial", label: "Commercial" },
  { key: "before-after", label: "Before & After" },
];

const searchSchema = z.object({
  category: z.string().optional(),
});

export const Route = createFileRoute("/gallery")({
  validateSearch: searchSchema,
  head: () => {
    const url = canonical("/gallery");
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: GalleryPage,
});

function GalleryPage() {
  const search = useSearch({ from: "/gallery" });
  const navigate = useNavigate({ from: "/gallery" });
  const category = search.category ?? "all";
  const [pageSize, setPageSize] = useState(24);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  const list = useServerFn(listGalleryAssets);
  const query = useQuery({
    queryKey: ["gallery", category, pageSize],
    queryFn: () => list({ data: { category, limit: pageSize, offset: 0 } }),
  });

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const hasMore = items.length < total;

  const setCategory = (k: string) => {
    setPageSize(24);
    navigate({ search: k === "all" ? {} : { category: k }, replace: false });
  };

  const close = useCallback(() => setLightboxIdx(null), []);
  const prev = useCallback(
    () => setLightboxIdx((i) => (i === null ? null : (i - 1 + items.length) % items.length)),
    [items.length],
  );
  const next = useCallback(
    () => setLightboxIdx((i) => (i === null ? null : (i + 1) % items.length)),
    [items.length],
  );

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, close, prev, next]);

  return (
    <>
      <section className="bg-primary py-16 text-white lg:py-20">
        <Container className="text-center">
          <h1 className="text-3xl font-bold sm:text-4xl lg:text-5xl">Our work across the Phoenix Valley</h1>
          <p className="mx-auto mt-4 max-w-2xl text-white/80">
            Real photos from real jobs — foam roofing, tile work, repairs, and recoats across the Phoenix Valley since 1989.
          </p>
        </Container>
      </section>

      <section className="border-b border-border bg-white">
        <Container className="py-4">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map((c) => {
              const active = c.key === category;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCategory(c.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${
                    active
                      ? "bg-primary text-white"
                      : "border border-border bg-white text-foreground hover:border-primary"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </Container>
      </section>

      <section className="bg-[var(--surface)] py-12">
        <Container>
          {query.isLoading ? (
            <p className="text-center text-[var(--muted-foreground)]">Loading…</p>
          ) : items.length === 0 ? (
            <PlaceholderGrid />
          ) : (
            <>
              <div className="columns-1 gap-4 md:columns-2 lg:columns-3 [&>button]:mb-4 [&>button]:break-inside-avoid">
                {items.map((a, i) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setLightboxIdx(i)}
                    className="group relative block w-full overflow-hidden rounded-xl shadow-sm"
                  >
                    <img
                      src={a.webpUrl ?? a.url}
                      alt={a.alt || "Durafoam Roofing project"}
                      width={a.width ?? undefined}
                      height={a.height ?? undefined}
                      loading="lazy"
                      className="block w-full"
                    />
                    {(a.caption || a.categories?.length) && (
                      <span className="absolute inset-0 hidden flex-col justify-end bg-gradient-to-t from-black/80 via-black/20 to-transparent p-4 text-left text-white opacity-0 transition group-hover:opacity-100 lg:flex">
                        {a.categories?.[0] && (
                          <span className="mb-1 inline-block w-fit rounded bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                            {a.categories[0]}
                          </span>
                        )}
                        {a.caption && <span className="text-sm">{a.caption}</span>}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              {hasMore && (
                <div className="mt-10 text-center">
                  <button
                    type="button"
                    onClick={() => setPageSize((p) => p + 24)}
                    className="rounded-lg border-2 border-primary bg-white px-6 py-3 text-sm font-semibold text-primary hover:bg-primary hover:text-white"
                  >
                    Load more photos
                  </button>
                </div>
              )}
            </>
          )}
        </Container>
      </section>

      {lightboxIdx !== null && items[lightboxIdx] && (
        <Lightbox
          asset={items[lightboxIdx]}
          onClose={close}
          onPrev={prev}
          onNext={next}
          index={lightboxIdx}
          total={items.length}
        />
      )}

      {/* CTA */}
      <section className="bg-primary py-14 text-white lg:py-16">
        <Container className="text-center">
          <h2 className="mx-auto max-w-3xl text-2xl font-bold sm:text-3xl">Like what you see?</h2>
          <p className="mx-auto mt-3 max-w-2xl text-white/80">
            Schedule a free roof inspection — we'll come take a look and give you a written estimate.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <ButtonLink href="/free-roof-inspection-phoenix" variant="primary" size="lg">
              Schedule free inspection
            </ButtonLink>
            <ButtonLink href="tel:+14809415373" variant="ghost-light" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" />
              Call 480-941-5373
            </ButtonLink>
          </div>
        </Container>
      </section>
    </>
  );
}

function PlaceholderGrid() {
  const heights = [240, 320, 280, 360, 240, 300, 340, 260, 320, 280, 360, 240];
  return (
    <div>
      <div className="columns-1 gap-4 md:columns-2 lg:columns-3 [&>div]:mb-4 [&>div]:break-inside-avoid">
        {heights.map((h, i) => (
          <div
            key={i}
            className="flex w-full items-center justify-center rounded-xl bg-gradient-to-br from-[#E0DDD6] to-[#F2EFE8] text-xs text-[var(--muted-foreground)]"
            style={{ height: h }}
          >
            Photo coming soon
          </div>
        ))}
      </div>
      <p className="mt-8 text-center text-sm text-[var(--muted-foreground)]">
        Photos coming soon — check back after launch.
      </p>
    </div>
  );
}

function Lightbox({
  asset,
  onClose,
  onPrev,
  onNext,
  index,
  total,
}: {
  asset: GalleryAsset;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  index: number;
  total: number;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/[0.92] p-4"
      onClick={onClose}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"
      >
        <X className="h-5 w-5" />
      </button>
      <button
        type="button"
        aria-label="Previous"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 grid h-12 w-12 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        type="button"
        aria-label="Next"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"
      >
        <ChevronRight className="h-6 w-6" />
      </button>
      <div className="max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <img
          src={asset.webpUrl ?? asset.url}
          alt={asset.alt || "Durafoam Roofing project"}
          className="max-h-[80vh] w-auto rounded-lg"
        />
        <div className="mt-4 flex items-center justify-between text-sm text-white/80">
          <div>
            {asset.categories?.[0] && (
              <span className="mr-2 rounded bg-accent px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide">
                {asset.categories[0]}
              </span>
            )}
            {asset.caption}
          </div>
          <div className="text-xs text-white/60">{index + 1} / {total}</div>
        </div>
      </div>
    </div>
  );
}
